<template>
    <div class="reatlas-container">
        <!-- Control Widget Bar (Top) -->
        <control-widget-bar
            :visible-site-count="totalSites"
            :total-site-count="reatlas.sites.all.length"
            :projects="projectsData"
            :loading-projects="loadingProjects"
            :control-panel-collapsed="controlPanelCollapsed"
            :active-layers-count="activeLayersCount"
            @toggle-control-panel="toggleMapControls"
            @mode-changed="onModeChanged"
            @project-selected="onProjectSelected"
        >
            <template #additional-controls>
                <sort-controls
                    :layout="layout"
                    :sort-by="sortBy"
                    :sort-direction="sortDirection"
                    @update:sortBy="sortBy = $event"
                    @update:sortDirection="sortDirection = $event"
                />
            </template>
        </control-widget-bar>

        <div class="reatlas-content">
            <!-- Active Filters Panel -->
            <active-filters-panel
                :active-filters="activeFiltersState"
                :show="showActiveFiltersPanel"
                @close="showActiveFiltersPanel = false"
                @clear-all="clearAllActiveFilters"
            >
                <template #filter-accordion="{ filter, isExpanded, toggle }">
                    <filter-accordion-item
                        :filter-type="filter.type"
                        :filter-label="filter.label"
                        :filter-icon="filter.icon"
                        :filter-data="filter.data"
                        :is-expanded="isExpanded"
                        @toggle="toggle"
                    >
                        <!-- Travel Time Panel -->
                        <travel-time-filter-panel
                            v-if="filter.type === 'travelTime'"
                            :show="true"
                            :filters="travelTimeFilters"
                            :buildings="availableBuildings"
                            :loading="layerLoading"
                            @update:filters="updateTravelTimeFilters"
                            @close="() => {}"
                        />
                        
                        <!-- Highway Panel -->
                        <highway-filter-panel
                            v-if="filter.type === 'highway'"
                            :selected-states="layers.highways.selectedStates || []"
                            :available-states="(reatlas.states || []).map(s => ({ code: s.state, name: s.name }))"
                            :on-load-highways="loadHighwaysForStates"
                        />
                        
                        <!-- Protest Panel -->
                        <protest-filter-panel
                            v-if="filter.type === 'protest'"
                            :filters="reatlas.protestFilters"
                            :show="true"
                            :protest-data="[]"
                            @update:filters="updateProtestFilters"
                        />
                        
                        <!-- Directions Panel -->
                        <directions-panel
                            v-if="filter.type === 'directions'"
                        />
                    </filter-accordion-item>
                </template>
            </active-filters-panel>

            <div class="reatlas-map-container">
                <!-- Transit Layer Dark Mode Notice -->
                <TransitNotice :show="activeLayerIds && activeLayerIds.includes('transit') && isDarkMode" />

                <!-- Zoom Level Notice -->
                <ZoomNotice :show="true" />

                <!-- Direction Tooltip -->
                <div 
                    v-if="showDirectionTooltip" 
                    class="reatlas-direction-tooltip"
                    :style="tooltipStyle"
                >
                    {{ directionTooltipText }}
                </div>

                <div 
                    id="map" 
                    class="reatlas-map" 
                    @mousemove="onMapMouseMove" 
                    @mouseleave="onMapMouseLeave"
                ></div>

                <!-- Stats Overlay - Bottom Left -->
                <div class="reatlas-stats-overlay">
                    <div class="reatlas-stats-overlay__stat">
                        <div class="reatlas-stats-overlay__stat-value">{{ totalSites }}</div>
                        <div class="reatlas-stats-overlay__stat-label">Total Sites</div>
                    </div>
                    <div class="reatlas-stats-overlay__stat">
                        <div class="reatlas-stats-overlay__stat-value">{{ formatCurrency(average_psf, 2) }}</div>
                        <div class="reatlas-stats-overlay__stat-label">Average PSF</div>
                    </div>
                </div>

                <reatlas-loading-overlay v-show="reatlas.mapLoading" :progress="loadProgress" />

                <!-- Layer Loading Overlay -->
                <div 
                    v-if="layerLoading" 
                    class="reatlas-layer-loading-overlay"
                >
                    <div class="reatlas-layer-loading-content">
                        <div class="reatlas-layer-loading-spinner"></div>
                        <p class="reatlas-layer-loading-text">Loading layer...</p>
                    </div>
                </div>

                <!-- Control Panel (Right Side) -->
                <control-panel
                    :collapsed="controlPanelCollapsed"
                    :active-layers-count="activeLayersCount"
                    :active-filters-count="0"
                    @toggle="onControlPanelToggle"
                >
                    <template #location-search>
                        <location-search
                            @location-selected="onLocationSelected"
                        />
                    </template>
                    <template #layers-panel>
                        <layer-panel
                            :current-zoom="zoom"
                            :active-layers="activeLayerIds"
                            @layer-toggled="handleLayerToggle"
                        />
                    </template>
                </control-panel>
            </div>

            <reatlas-building-list
                :layout="layout"
                :sorted-buildings="filteredBuildings"
                :selected-site="selectedSite"
                :selected-comp="reatlas.selectedComp"
                :total-sites="totalSites"
                :total-comps="totalComps"
                :average-psf="average_psf"
                :average-ask-price-psf="average_ask_price_psf"
                :selected-project-id="selectedProjectId"
                :scroll-up-indicator-position="scrollUpIndicatorPosition"
                :scroll-down-indicator-position="scrollDownIndicatorPosition"
                :loading="sitesLoading"
                @show-site="showSite"
                @show-comp="showComp"
                @search="handleSearch"
            />

            <reatlas-detail-panel
                v-if="showDetailPanel"
                :selected-site="selectedSite"
                :selected-comp="selectedComp"
                :layout="layout"
                :loading="siteDetailLoading"
                @close="hideSideBar"
            />
        </div>

        <notifications classes="notification" position="bottom right"></notifications>

        <!-- Layer & marker popups (v1 style) -->
        <AirportPopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'airport'"
            :airport="reatlas.activePopup && reatlas.activePopup.data"
            @close="$store.commit('clearActivePopup')"
        />
        <PortPopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'port'"
            :port="reatlas.activePopup && reatlas.activePopup.data"
            @close="$store.commit('clearActivePopup')"
        />
        <WaterwayPopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'waterway'"
            :waterway="reatlas.activePopup && reatlas.activePopup.data"
            @close="$store.commit('clearActivePopup')"
        />
        <RailPopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'rail'"
            :rail="reatlas.activePopup && reatlas.activePopup.data"
            @close="$store.commit('clearActivePopup')"
        />
        <HighwayPopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'highway'"
            :highway="reatlas.activePopup && reatlas.activePopup.data"
            @close="$store.commit('clearActivePopup')"
        />
        <WildfirePopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'wildfire'"
            :wildfire="reatlas.activePopup && reatlas.activePopup.data"
            @close="$store.commit('clearActivePopup')"
        />
        <ProtestPopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'protest'"
            :protest="reatlas.activePopup && reatlas.activePopup.data"
            :filters="reatlas.protestFilters || {}"
            @close="$store.commit('clearActivePopup')"
        />
        <CustomerPopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'customer'"
            :customer="reatlas.activePopup && reatlas.activePopup.data"
            @close="$store.commit('clearActivePopup')"
        />
        <TeamPopup
            :show="reatlas.activePopup && reatlas.activePopup.type === 'team'"
            :member="reatlas.activePopup && reatlas.activePopup.data"
            @close="$store.commit('clearActivePopup')"
        />
    </div>
</template>

<script>
    import { mapState, mapGetters, mapActions } from 'vuex';
    import { Loader } from "@googlemaps/js-api-loader";
    import { DARK_MAP_STYLES } from '../cresiteiq2/constants.js';

    export default {
        name: 'ThemedReatlas',
        
        components: {
            // KAN-173: All async for code splitting
            ControlWidgetBar: () => import('../cresiteiq2/ControlWidgetBar.vue'),
            ControlPanel: () => import('../cresiteiq2/ControlPanel.vue'),
            LocationSearch: () => import('../cresiteiq2/LocationSearch.vue'),
            LayerPanel: () => import('../cresiteiq2/LayerPanel.vue'),
            TransitNotice: () => import('../cresiteiq2/TransitNotice.vue'),
            ZoomNotice: () => import('../cresiteiq2/ZoomNotice.vue'),
            ActiveFiltersPanel: () => import('../cresiteiq2/ActiveFiltersPanel.vue'),
            FilterAccordionItem: () => import('../cresiteiq2/FilterAccordionItem.vue'),
            DirectionsPanel: () => import('../cresiteiq2/DirectionsPanel.vue'),
            TravelTimeFilterPanel: () => import('../cresiteiq2/TravelTimeFilterPanel.vue'),
            HighwayFilterPanel: () => import('../cresiteiq2/HighwayFilterPanel.vue'),
            ProtestFilterPanel: () => import('../cresiteiq2/ProtestFilterPanel.vue'),
            SortControls: () => import('./SortControls.vue'),
            ReatlasBuildingList: () => import('./ReatlasBuildingList.vue'),
            ReatlasDetailPanel: () => import('./ReatlasDetailPanel.vue'),
            ReatlasLoadingOverlay: () => import('./ReatlasLoadingOverlay.vue'),
            AirportPopup:  () => import('../cresiteiq2/AirportPopup.vue'),
            PortPopup:     () => import('../cresiteiq2/PortPopup.vue'),
            WaterwayPopup: () => import('../cresiteiq2/WaterwayPopup.vue'),
            RailPopup:     () => import('../cresiteiq2/RailPopup.vue'),
            HighwayPopup:  () => import('../cresiteiq2/HighwayPopup.vue'),
            WildfirePopup: () => import('../cresiteiq2/WildfirePopup.vue'),
            ProtestPopup:  () => import('../cresiteiq2/ProtestPopup.vue'),
            CustomerPopup: () => import('../cresiteiq2/CustomerPopup.vue'),
            TeamPopup:     () => import('../cresiteiq2/TeamPopup.vue'),
        },

        props: {
            setMode: {
                type: String,
                required: true
            },
            siteData: {
                type: Array,
                required: false
            },
            selectedViewOption: {
                type: String,
                default: 'site'
            },
            loadProjectId: {
                type: Number,
                required: false
            },
            setZoom: {
                type: Number,
                default: 6
            },
            showFilters: {
                type: Boolean,
                default: true
            },
            showArchivedSites: {
                type: Boolean,
                default: false
            },
            loadSavedMapId: {
                type: Number,
                required: false
            }
        },

        data() {
            return {
                loader: null,
                mode: this.setMode,
                selectedProjectId: this.loadProjectId || "",
                center: { lat: 39.8097343, lng: -98.5556199 },
                rotation: 0,
                zoom: this.setZoom,
                loading: false,
                layerLoading: false,
                scrollUpIndicatorPosition: 0,
                scrollDownIndicatorPosition: 0,
                sortBy: "item.building.address_line_1",
                sortDirection: 'asc',
                tooltipX: 0,
                tooltipY: 0,
                tooltipVisible: false,
                searchQuery: '',
                isDarkMode: false,
                themeObserver: null,
                controlPanelCollapsed: true,
                projectsData: [],
                loadingProjects: false,
                showActiveFiltersPanel: true,
                siteDetailLoading: false,
                sitesLoading: false,
                loadProgress: 0,
            }
        },

        async mounted() {
            this.reatlas.layout = this.selectedViewOption;

            // Detect dark mode for map styling
            this.detectDarkMode();
            
            // Watch for theme changes
            this.observeThemeChanges();

            // Load projects for dropdown (non-blocking)
            this.loadProjectsData();

            this.loader = new Loader({
                apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
                version: "quarterly",
                libraries: ["places"]
            });

            document.querySelector("html").classList.add('overflow-x-hidden');

            // KAN-173: Init map and fetch sites in parallel — map renders immediately,
            // markers are added once both are ready.
            this.sitesLoading = true;
            this._startProgress();
            const [sites] = await Promise.all([
                (this.siteData && this.siteData.length > 0
                    ? Promise.resolve(this.siteData)
                    : axios.get('/api/sites/map').then(r => r.data).catch(() => [])),
                this.initMap(),
            ]);
            this._finishProgress();
            this.sitesLoading = false;

            this.reatlas.sites.all = sites;
            this.reatlas.sites.filtered = sites.filter(site => site.archived_at == null);

            if (this.loadSavedMapId != null) {
                this.$store.dispatch('loadMap', this.loadSavedMapId);
            }

            this.$store.dispatch('loadStates');

            this.$nextTick(async () => {
                this.initScrollListener();
                this.setScrollIndicatorPosition();
                await this.$store.dispatch('addMarkers', { data: this.reatlas.sites.filtered, type: 'site' });
                this.$emit('filterRequest');

                this.reatlas.mapLoading = false;
            })
        },

        watch: {
            'reatlas.layerLoading'(newValue) {
                this.layerLoading = newValue;
            },

            'layers.travel_time.parameters.travel_type'() {
                this.$store.dispatch('fetchTravelTime');
            },

            'reatlas.filteredMarkers'() {
                if (this.reatlas.layout == 'site') {
                    var div = document.getElementById('site-card-container');
                } else if (this.reatlas.layout == 'projects') {
                    div = document.getElementById('comp-card-container');
                }

                if (! div) {
                    return;
                }

                div.dispatchEvent(new CustomEvent('scroll'));
            },

            'reatlas.shapes'() {
                this.$store.dispatch('hideBuildingsNotInShapes');
                this.$store.dispatch('syncBuildings');
            },

            'ksds.selectedScenarioId'() {
                this.recalculateCompScores();
            },

            selectedSite() {
                this.$store.dispatch('panTo', this.selectedSite.building_id);
            },

            selectedComp() {
                if (this.selectedComp.building_id) {
                    _.debounce(() => {
                        this.$store.dispatch('panTo', this.selectedComp.building_id);
                    }, 100)();
                }
            },

            'creSiteIQ.boundary'() {
                this.$store.dispatch('remapCRESiteIQData');
            },

            activeFiltersState: {
                handler(newFilters) {
                    const hasFilters = Object.keys(newFilters).length > 0;
                    
                    // Auto-show panel when filters are added
                    if (hasFilters && !this.showActiveFiltersPanel) {
                        this.showActiveFiltersPanel = true;
                    }
                    
                    // Auto-hide panel when all filters are removed
                    if (!hasFilters && this.showActiveFiltersPanel) {
                        this.showActiveFiltersPanel = false;
                    }
                },
                deep: true
            }
        },

        computed: {
            hasActiveProtestFilters() {
                const { selectedFactors = [], searchText = '', dateRange = null } = this.reatlas.protestFilters || {};
                return selectedFactors.length > 0 || 
                       (searchText && searchText.trim() !== '') || 
                       (dateRange && (dateRange.startDate || dateRange.endDate));
            },
            
            ...mapState(['users', 'reatlas', 'creSiteIQ', 'projects', 'ksds']),
            ...mapGetters(['addedMarkers', 'scenario', 'scenarioKsds']),
            ...mapActions(['addMarkers', 'addMarker', 'centerWithBounds']),

            map() {
                return this.reatlas.map;
            },

            layout() {
                return this.reatlas.layout || this.selectedViewOption || 'site';
            },

            selectedSite() {
                return this.reatlas.selectedSite || {};
            },

            selectedComp() {
                return this.reatlas.selectedComp || {};
            },

            showDetailPanel() {
                return this.siteDetailLoading ||
                       Object.keys(this.selectedSite).length > 0 || 
                       (this.layout == 'projects' && Object.keys(this.selectedComp).length > 0);
            },

            sortedBuildings() {
                let buildings = this.reatlas.filteredMarkers.filter(marker =>
                    ['site', 'comp'].includes(marker.type)
                );

                if (this.layout === 'projects') {
                    buildings = buildings.filter(marker => marker.type === 'comp');
                }

                const uniqueMap = new Map();
                for (const b of buildings) {
                    uniqueMap.set(`${b.item.id}-${b.type}`, b);
                }

                return _.orderBy(
                    [...uniqueMap.values()],
                    building => _.at(building, this.sortBy)[0],
                    this.sortDirection
                );
            },

            filteredBuildings() {
                if (!this.searchQuery || this.searchQuery.trim() === '') {
                    return this.sortedBuildings;
                }

                const query = this.searchQuery.toLowerCase();
                
                return this.sortedBuildings.filter(building => {
                    const item = building.item;
                    
                    // Search in address
                    const address = this.layout === 'site' 
                        ? item.building?.address_line_1 
                        : item.address_line_1;
                    if (address && address.toLowerCase().includes(query)) {
                        return true;
                    }
                    
                    // Search in city
                    const city = this.layout === 'site' 
                        ? item.building?.city 
                        : item.city;
                    if (city && city.toLowerCase().includes(query)) {
                        return true;
                    }
                    
                    // Search in state
                    const state = this.layout === 'site' 
                        ? item.building?.state 
                        : item.state;
                    if (state && state.toLowerCase().includes(query)) {
                        return true;
                    }
                    
                    return false;
                });
            },

            layers() {
                return this.reatlas.layers;
            },

            buildings() {
                var buildings = _.filter(this.reatlas.filteredMarkers, marker => marker.type != 'employee');
                return _.orderBy(buildings, building => _.at(building, 'item.building.address_line_1')[0], 'asc');
            },

            totalSites() {
                return _.filter(this.reatlas.filteredMarkers, marker => marker.type == 'site').length || 0;
            },

            totalComps() {
                return _.filter(this.reatlas.filteredMarkers, marker => marker.type == 'comp').length || 0;
            },

            average_psf() {
                return _.meanBy(_.filter(this.reatlas.filteredMarkers, marker => marker.type == 'site'), marker => marker.item.current_psf);
            },

            average_ask_price_psf() {
                if (! this.reatlas.filteredMarkers) {
                    return 0;
                }

                return _.meanBy(_.filter(this.reatlas.filteredMarkers, marker => marker.type == 'comp'), (comp) => {
                    return comp.item.pivot.ask_price;
                });
            },

            canLoadCRESiteIQ() {
                if (this.map?.getZoom() <= this.creSiteIQ.minZoom) {
                    return 'disabled';
                }
                return null;
            },

            activeLayersCount() {
                if (!this.layers) return 0;
                return Object.values(this.layers).filter(layer => layer.isOn === true).length;
            },

            activeLayerIds() {
                if (!this.layers) return [];
                return Object.keys(this.layers).filter(key => this.layers[key]?.isOn === true);
            },

            activeFiltersState() {
                const filters = {};
                
                // Travel Time filter - only show when layer is active
                if (this.activeLayerIds.includes('travel_time')) {
                    filters.travelTime = {
                        enabled: true,
                        label: 'Travel Time',
                        icon: 'clock',
                        data: this.layers.travel_time.parameters || {}
                    };
                }
                
                // Highway filter - only show when layer is active
                if (this.activeLayerIds.includes('highways')) {
                    filters.highway = {
                        enabled: true,
                        label: 'Highway',
                        icon: 'road',
                        data: {
                            selectedStates: this.layers.highways.selectedStates || [],
                            availableStates: (this.reatlas.states || []).map(s => ({ code: s.state, name: s.name }))
                        }
                    };
                }
                
                // Protest filter - only show when layer is active
                if (this.activeLayerIds.includes('protests')) {
                    filters.protest = {
                        enabled: true,
                        label: 'Protest',
                        icon: 'alert',
                        data: this.reatlas.protestFilters || {}
                    };
                }
                
                // Directions filter - only show when layer is active
                if (this.activeLayerIds.includes('directions')) {
                    filters.directions = {
                        enabled: true,
                        label: 'Route Planning',
                        icon: 'navigation',
                        data: {}
                    };
                }
                
                return filters;
            },

            showDirectionTooltip() {
                return this.reatlas.layers.directions.isOn && this.tooltipVisible;
            },
            
            directionTooltipText() {
                if (!this.reatlas.directions.stops[0] || !this.reatlas.directions.stops[0].latLng) {
                    return 'Click to select Origin';
                } else {
                    return 'Click to select Destination';
                }
            },
            
            tooltipStyle() {
                return {
                    left: this.tooltipX + 16 + 'px',
                    top: this.tooltipY + 16 + 'px',
                    position: 'fixed',
                    pointerEvents: 'none',
                    transition: 'left 0.05s, top 0.05s',
                };
            },

            /**
             * Get travel time filters in the format expected by TravelTimeFilterPanel
             */
            travelTimeFilters() {
                const params = this.layers.travel_time.parameters;
                return {
                    travelMode: params.travel_type || 'driving',
                    travelTime: params.travel_time || 1800,
                    buildingId: params.building_id || null,
                    arriveOrDepart: params.arrive_or_depart || 'depart',
                    arriveOrDepartTime: params.arrive_or_depart_time || '7'
                };
            },

            /**
             * Get available buildings for travel time selection
             * Uses all markers, not just filtered ones, so dropdown always shows all buildings
             */
            availableBuildings() {
                // Get all site and comp buildings from ALL markers (not just filtered)
                const buildings = this.reatlas.markers
                    .filter(marker => ['site', 'comp'].includes(marker.type))
                    .map(marker => {
                        const building = marker.type === 'site' 
                            ? marker.item.building 
                            : marker.item;
                        
                        if (!building) return null;
                        
                        return {
                            id: building.id,
                            address: building.address_line_1 || building.address,
                            city: building.city,
                            state: building.state,
                            latitude: building.latitude,
                            longitude: building.longitude
                        };
                    })
                    .filter(building => building && building.id);

                // Remove duplicates by id
                const uniqueBuildings = [];
                const seenIds = new Set();
                
                for (const building of buildings) {
                    if (!seenIds.has(building.id)) {
                        seenIds.add(building.id);
                        uniqueBuildings.push(building);
                    }
                }

                return uniqueBuildings;
            },
        },

        methods: {
            handleSearch(query) {
                this.searchQuery = query;
            },

            _startProgress() {
                this.loadProgress = 0;
                clearInterval(this.$_progressTimer);
                this.$_progressTimer = setInterval(() => {
                    if (this.loadProgress < 90) {
                        this.loadProgress += (90 - this.loadProgress) * 0.06;
                    }
                }, 80);
            },

            _finishProgress() {
                clearInterval(this.$_progressTimer);
                this.$_progressTimer = null;
                this.loadProgress = 100;
                setTimeout(() => { this.loadProgress = 0; }, 400);
            },

            clearProtestFilters() {
                if (this.$refs.protestFilters) {
                    const filters = this.$refs.protestFilters.clearFilters();
                    
                    if (window.applyProtestMapFilters) {
                        window.applyProtestMapFilters(filters);
                    }
                }
            },
            
            async initMap() {
                this.loader.load().then(async () => {
                    const { Map, InfoWindow } = await google.maps.importLibrary("maps");

                    const mapStyles = this.isDarkMode ? DARK_MAP_STYLES : [];
                    console.log('REatlas - Initializing map. Dark mode:', this.isDarkMode);

                    this.reatlas.map = new Map(document.getElementById("map"), {
                        center: this.center,
                        zoom: this.zoom,
                        styles: mapStyles,
                        mapTypeId: 'roadmap',
                        // Note: mapId removed to enable custom dark mode styles
                        // Advanced Markers will still work but show a console warning
                        zoomControl: false,
                        mapTypeControl: false,
                        streetViewControl: false,
                        fullscreenControl: true,
                        fullscreenControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
                        scaleControlOptions:      { position: google.maps.ControlPosition.LEFT_BOTTOM }
                    });
                    
                    console.log('REatlas - Map created successfully');

                    this.reatlas.infoWindow = new InfoWindow({ headerDisabled: true });

                    this.reatlas.map.addListener("dragend", async () => {
                        this.$store.dispatch('hideBuildingsNotInBounds');

                        if (this.reatlas.layers.cresiteiq.isOn) {
                            this.$store.dispatch('refreshScores');
                        }
                    });

                    this.reatlas.map.addListener("zoom_changed", async () => {
                        if (this.reatlas.layers.cresiteiq.isOn) {
                            this.$store.dispatch('refreshScores');
                        }
                    });

                    this.$store.dispatch('initDataLayerListeners');

                    this.reatlas.map.addListener("idle", () => {
                        if (this.reatlas.heatMap != null) {
                            let zoom = this.reatlas.map.getZoom();

                            var radius = null;
                            var maxIntensity = 0;
                            var opacity = 0.5;

                            if (zoom < 5) {
                                maxIntensity = 500;
                                radius = 20;
                            } else if (zoom == 5) {
                                radius = 20;
                            } else if (zoom == 6) {
                                radius = 30;
                            } else if (zoom == 7) {
                                radius = 50;
                            } else if (zoom == 8) {
                                radius = 100;
                            } else if (zoom == 9) {
                                radius = 150;
                            } else if (zoom == 10) {
                                radius = 200;
                            } else if (zoom == 11) {
                                radius = 400;
                            } else if (zoom == 12) {
                                radius = 700;
                                opacity = 0.4;
                            } else {
                                radius = 1000;
                            }

                            this.reatlas.heatMap.setOptions({ radius: radius, maxIntensity: maxIntensity, opacity: opacity });
                        }
                        this.$store.dispatch('hideBuildingsNotInBounds');
                    });

                    this.initDrawingManager();

                    if (this.mode == 'cresiteiq') {
                        this.$store.dispatch('toggleLayer', 'cresiteiq');
                    }
                });

                window.closeInfoBubble = () => this.closeInfoBubble();
                window.closeCRESiteIQInfoBubble = () => this.closeCRESiteIQInfoBubble();
                window.showSite = () => this.showSite();
                window.showComp = () => this.showComp();
                window.toggleRadius = (el) => this.toggleRadius(el);
                window.toggleLayer = (layerKey) => this.$store.dispatch('toggleLayer', layerKey);
            },

            async initDrawingManager() {
                // Google removed DrawingManager in Maps JS 3.65. The loader is pinned to the
                // quarterly channel, which still has it, but that channel will roll forward.
                // Degrade to "no drawing tools" rather than an unhandled rejection.
                try {
                    await google.maps.importLibrary("drawing");

                    this.reatlas.drawingManager = new google.maps.drawing.DrawingManager({
                        drawingControl: true,
                        drawingControlOptions: {
                            position: google.maps.ControlPosition.LEFT_BOTTOM,
                            drawingModes: [
                                google.maps.drawing.OverlayType.CIRCLE,
                                google.maps.drawing.OverlayType.POLYGON,
                                google.maps.drawing.OverlayType.RECTANGLE,
                            ],
                        },
                        circleOptions: {
                            strokeColor: "#2563eb",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: "#60a5fa",
                            fillOpacity: 0.35,
                            clickable: true,
                            editable: true,
                            zIndex: 1,
                        },
                        polygonOptions: {
                            strokeColor: "#2563eb",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: "#60a5fa",
                            fillOpacity: 0.35,
                            clickable: true,
                            editable: true,
                            zIndex: 1,
                        },
                        rectangleOptions: {
                            strokeColor: "#2563eb",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: "#60a5fa",
                            fillOpacity: 0.35,
                            clickable: true,
                            editable: true,
                            zIndex: 1,
                        },
                    });

                    this.reatlas.drawingManager.setMap(this.reatlas.map);

                    google.maps.event.addListener(this.reatlas.drawingManager, 'overlaycomplete', shape => {
                        this.$store.dispatch('addShape', shape);
                    });
                } catch (error) {
                    console.warn("Drawing tools unavailable:", error && error.message);
                }
            },

            initScrollListener() {
                let upIndicators = document.getElementsByClassName('scroll-up-indicator');
                let downIndicators = document.getElementsByClassName('scroll-down-indicator');

                if (this.reatlas.layout == 'site') {
                    var div = document.getElementById('site-card-container');
                } else if (this.reatlas.layout == 'projects') {
                    div = document.getElementById('comp-card-container');
                }

                if (!div) return;

                div.addEventListener("scroll", () => {
                    if (div.scrollTop == 0) {
                        upIndicators.forEach((indicator) => {
                            indicator.classList.add('hidden');
                        });
                    } else {
                        upIndicators.forEach((indicator) => {
                            indicator.classList.remove('hidden');
                        });
                    }

                    if (this.reatlas.filteredMarkers.length < 7 || (div.scrollTop != 0 && (div.scrollHeight - div.scrollTop == div.clientHeight))) {
                        downIndicators.forEach((indicator) => {
                            indicator.classList.add('hidden');
                        });
                    } else {
                        downIndicators.forEach((indicator) => {
                            indicator.classList.remove('hidden');
                        });
                    }

                    this.setScrollIndicatorPosition();
                });

                window.addEventListener("resize", () => {
                    this.setScrollIndicatorPosition();
                });
            },

            setScrollIndicatorPosition() {
                if (this.reatlas.layout == 'site') {
                    var div = document.getElementById('site-card-container');
                } else if (this.reatlas.layout == 'projects') {
                    div = document.getElementById('comp-card-container');
                }

                if (!div) return;

                this.scrollUpIndicatorPosition = div.scrollTop + 10;
                this.scrollDownIndicatorPosition = div.scrollTop + div.clientHeight - 40;
            },

            async setView(type) {
                this.hideSideBar();
                this.closeInfoBubble();

                // Normalize type: 'sites' -> 'site', 'projects' -> 'projects'
                const markerType = type === 'sites' ? 'site' : type;
                const layoutType = type === 'sites' ? 'site' : type;

                if (type == 'projects') {
                    this.sortBy = 'item.pivot.score';
                    this.sortDirection = 'desc';

                    this.loading = true;
                    this.$store.dispatch('loadProjects').then(() => {
                        this.loading = false;
                    });

                    this.updatePageTitle('Projects')

                    var dataset = this.projects.selectedProject.comps;
                } else {
                    this.selectedProjectId = '';
                    this.sortBy = 'item.building.address_line_1';
                    this.sortDirection = 'asc';

                    this.updatePageTitle('Sites')

                    dataset = this.reatlas.sites.filtered;
                }

                this.reatlas.layout = layoutType;

                this.$store.dispatch('removeMarkersByType', 'site');
                this.$store.dispatch('removeMarkersByType', 'comp');
                this.$store.dispatch('removeCircles');
                this.$store.dispatch('removePolygonsByType', 'travel_time');

                this.reatlas.layers.travel_time.parameters.building_id = '';

                await this.$store.dispatch('addMarkers', { data: dataset, type: markerType });

                // Only center with bounds if we have markers
                if (dataset && dataset.length > 0) {
                    this.$store.dispatch('centerWithBounds');
                }
            },

            closeInfoBubble() {
                this.reatlas.selectedComp = {};
                this.reatlas.infoWindow.close();
                this.hideSideBar();
            },

            closeCRESiteIQInfoBubble() {
                this.$store.dispatch('resetFeatures');
                this.reatlas.infoWindow.close();
            },

            async showSite(site) {
                let data = site || this.reatlas.featureData;
                this.reatlas.selectedComp = {};
                this.updatePageTitle(data.building.address_line_1);
                // If already showing this site with full data (has media), reuse it.
                if (this.reatlas.selectedSite?.id === data.id && this.reatlas.selectedSite?.media !== undefined) {
                    return;
                }
                // Show panel immediately with loading spinner, then swap in full data.
                this.reatlas.selectedSite = {};
                this.siteDetailLoading = true;
                try {
                    const response = await axios.get(`/api/site/${data.id}`);
                    this.reatlas.selectedSite = response.data;
                } catch (e) {
                    console.error('[REAtlas] Failed to load full site detail:', e);
                    this.reatlas.selectedSite = data;
                } finally {
                    this.siteDetailLoading = false;
                }
            },

            showComp(comp) {
                let data = comp || this.reatlas.featureData;
                this.reatlas.selectedSite = {};
                this.reatlas.selectedComp = data.pivot;
                this.reatlas.selectedComp.building = data;
                this.updatePageTitle(data.address_line_1);
            },

            toggleRadius(el) {
                let circle = _.find(this.reatlas.circles, circle => {
                    return circle.marker.building_id == this.reatlas.featureData.building_id;
                });

                let circleIcon = el.getElementsByTagName('span')[0];

                if (circle) {
                    el.classList.remove('bg-gray-500', 'hover:bg-blue-100');
                    el.classList.add('bg-gray-100', 'hover:bg-blue-500');
                    circleIcon.classList.add('border-blue-400', 'group-hover:border-white');
                    circleIcon.classList.remove('border-white', 'group-hover:border-gray-500');

                    circle.ring.setMap(null);
                    this.reatlas.circles = _.remove(this.reatlas.circles, circle => {
                        return circle.marker.building_id != this.reatlas.featureData.building_id;
                    });
                } else {
                    el.classList.remove('bg-gray-100', 'hover:bg-blue-500');
                    el.classList.add('bg-blue-500', 'hover:bg-gray-100');
                    circleIcon.classList.add('border-white', 'group-hover:border-gray-500');
                    circleIcon.classList.remove('border-blue-400', 'group-hover:border-white');

                    this.$store.dispatch('drawCircle', {
                        item: this.reatlas.featureData,
                        position: {
                            lat: this.reatlas.featureData.building?.latitude || this.reatlas.featureData.latitude,
                            lng: this.reatlas.featureData.building?.longitude || this.reatlas.featureData.longitude,
                        },
                        miles: 10
                    });
                }
            },

            async loadProject() {
                this.hideSideBar();
                this.loading = true;

                await this.$store.dispatch('loadProject', this.selectedProjectId);

                this.ksds.scenarios = this.projects.selectedProject.ksd_scenarios;

                let defaultScenario = _.find(this.ksds.scenarios, ['is_default', 1]);

                this.ksds.selectedScenarioId = defaultScenario ? defaultScenario.id : this.ksds.scenarios[0].id;
                this.reatlas.isNegotiationMode = this.scenario.negotiation_mode;

                this.updatePageTitle(this.projects.selectedProject.name);

                this.$store.dispatch('removeMarkersByType', 'site');
                this.$store.dispatch('removeMarkersByType', 'comp');

                await this.$store.dispatch('addMarkers', { data: this.projects.selectedProject.comps, type: 'comp' });
                await this.$store.dispatch('addMarkers', { data: this.projects.selectedProject.sites, type: 'site' });
                await this.$store.dispatch('showMarkersInBounds');

                // Only center with bounds if we have markers
                const hasMarkers = (this.projects.selectedProject.comps && this.projects.selectedProject.comps.length > 0) ||
                                   (this.projects.selectedProject.sites && this.projects.selectedProject.sites.length > 0);
                if (hasMarkers) {
                    this.$store.dispatch('centerWithBounds');
                }

                this.loading = false;
            },

            async loadScenario() {
                if (! this.scenario) {
                    return;
                }

                this.reatlas.isNegotiationMode = this.scenario.negotiation_mode;
                this.reatlas.infoWindow.close();
            },

            recalculateCompScores() {
                _.each(this.reatlas.filteredMarkers, comp => {
                    comp.item.pivot.score = this.calculateCompScore(comp.item.pivot.id);
                    comp.item.pivot.score_badge_color = this.getScoreBadgeColor(comp.item.pivot.score);
                    comp.item.pivot.score_color = this.getScoreColor(comp.item.pivot.score);
                });
            },

            calculateCompScore(compId) {
                var compScore = 0;
                const priceKSD = _.find(this.scenarioKsds, ['driver', 'Price']);
                const priceWeight = priceKSD ? parseInt(priceKSD.weight) : 0;

                _.map(this.scenarioKsds, ksd => {
                    let scoreRecord = _.find(ksd.scores, ['scorable_id', compId]);
                    const score = scoreRecord ? parseInt(scoreRecord['score']) : 0;
                    const weight = parseInt(ksd.weight);
                    const divisble = 100 - priceWeight;

                    const scorePercentage = score * 20;
                    const weightedScore = (scorePercentage * weight) / divisble;

                    compScore += weightedScore;
                });

                compScore = Math.round(compScore);

                return compScore > 100 ? 100 : compScore;
            },

            getScoreColor(score) {
                return (score < 70 ? 'bg-red-500' : (score >= 90 ? 'bg-green-500' : 'bg-yellow-200 text-yellow-700'));
            },

            getScoreBadgeColor(score) {
                if (score < 70) {
                    return 'badge-red';
                }

                if (score >= 90) {
                    return 'badge-green';
                }

                if (score >= 70) {
                    return 'badge-yellow';
                }

                return 'badge-red';
            },

            updatePageTitle(title) {
                document.title = 'REatlas - ' + title;
            },

            hideSideBar() {
                this.reatlas.selectedComp = {};
                this.reatlas.selectedSite = {};
            },

            toggleSortDirection() {
                this.sortDirection = this.sortDirection == 'asc' ? 'desc' : 'asc';
            },

            toggleMapControls() {
                this.controlPanelCollapsed = !this.controlPanelCollapsed;
            },

            onControlPanelToggle(collapsed) {
                this.controlPanelCollapsed = collapsed;
            },

            onLocationSelected(location) {
                // Pan map to selected location
                if (this.map && location.lat && location.lng) {
                    this.map.panTo({ lat: location.lat, lng: location.lng });
                    
                    // Optionally zoom in to the location
                    if (location.zoom) {
                        this.map.setZoom(location.zoom);
                    } else {
                        this.map.setZoom(15); // Default zoom level
                    }
                }
            },

            async loadProjectsData() {
                this.loadingProjects = true;
                try {
                    // KAN-173: Use slim map endpoint — full project detail loads on-click via loadProject
                    await this.$store.dispatch('loadProjectsMap');
                    this.projectsData = this.$store.getters.projectsSortedBy('name');
                } catch (error) {
                    console.error('Failed to load projects:', error);
                } finally {
                    this.loadingProjects = false;
                }
            },

            onModeChanged(mode) {
                console.log('Mode changed to:', mode);
                this.setView(mode);
            },

            onProjectSelected(projectId) {
                console.log('Project selected:', projectId);
                if (projectId) {
                    this.selectedProjectId = projectId;
                    this.loadProject();
                }
            },

            onMapMouseMove(e) {
                this.tooltipX = e.clientX;
                this.tooltipY = e.clientY;
                this.tooltipVisible = true;
            },
            
            onMapMouseLeave() {
                this.tooltipVisible = false;
            },

            handleLayerToggle({ layerId, enabled }) {
                console.log('Layer toggle:', layerId, enabled);
                this.$store.dispatch('toggleLayer', layerId);
            },

            clearAllActiveFilters() {
                // Turn off all layers that have filters
                if (this.activeLayerIds.includes('travel_time')) {
                    this.$store.dispatch('toggleLayer', 'travel_time');
                }
                if (this.activeLayerIds.includes('highways')) {
                    this.$store.dispatch('toggleLayer', 'highways');
                }
                if (this.activeLayerIds.includes('protests')) {
                    this.$store.dispatch('toggleLayer', 'protests');
                }
                if (this.activeLayerIds.includes('directions')) {
                    this.$store.dispatch('toggleLayer', 'directions');
                }
            },

            updateTravelTimeFilters(filters) {
                // Map TravelTimeFilterPanel format to store format
                const storeFilters = {
                    travel_type: filters.travelMode || 'driving',
                    travel_time: filters.travelTime || 1800,
                    building_id: filters.buildingId || '',
                    arrive_or_depart: filters.arriveOrDepart || 'depart',
                    arrive_or_depart_time: filters.arriveOrDepartTime || '7'
                };
                
                // Update travel time parameters in store
                Object.assign(this.layers.travel_time.parameters, storeFilters);
                
                // Pan to and zoom to the selected building
                if (storeFilters.building_id) {
                    this.$store.dispatch('panTo', storeFilters.building_id);
                    
                    // Fetch travel time data after panning
                    this.$nextTick(() => {
                        this.$store.dispatch('fetchTravelTime');
                    });
                }
            },

            loadHighwaysForStates(states) {
                // Load highways for selected states
                this.$store.dispatch('loadHighways', states);
            },

            updateProtestFilters(filters) {
                // Update protest filters in store
                this.$store.commit('setProtestFilters', filters);
                // Re-apply filters to protest markers
                if (window.applyProtestMapFilters) {
                    window.applyProtestMapFilters(filters);
                }
            },

            /**
             * Detect if dark mode is currently active
             */
            detectDarkMode() {
                const htmlElement = document.documentElement;
                const bodyElement = document.body;
                
                this.isDarkMode = htmlElement.classList.contains('dark') || 
                                  bodyElement.classList.contains('dark') ||
                                  htmlElement.getAttribute('data-theme') === 'dark';
                
                console.log('REatlas - Dark mode detected:', this.isDarkMode);
            },

            /**
             * Watch for theme changes and update map accordingly
             */
            observeThemeChanges() {
                this.themeObserver = new MutationObserver(() => {
                    const wasDark = this.isDarkMode;
                    this.detectDarkMode();
                    
                    // Update map theme if it changed
                    if (wasDark !== this.isDarkMode && this.reatlas.map) {
                        console.log('REatlas - Theme changed to:', this.isDarkMode ? 'dark' : 'light');
                        this.updateMapTheme(this.isDarkMode);
                    }
                });

                // Observe both html and body for class/attribute changes
                this.themeObserver.observe(document.documentElement, {
                    attributes: true,
                    attributeFilter: ['class', 'data-theme']
                });

                this.themeObserver.observe(document.body, {
                    attributes: true,
                    attributeFilter: ['class', 'data-theme']
                });
            },

            /**
             * Update map theme styles
             */
            updateMapTheme(isDark) {
                if (!this.reatlas.map) return;
                
                // Only apply theme styles if not in satellite mode
                const mapTypeId = this.reatlas.map.getMapTypeId();
                if (mapTypeId !== 'satellite') {
                    this.reatlas.map.setOptions({
                        styles: isDark ? DARK_MAP_STYLES : []
                    });
                    console.log('REatlas - Map theme updated:', isDark ? 'dark' : 'light');
                }
            },
        },

        beforeDestroy() {
            if (this.themeObserver) {
                this.themeObserver.disconnect();
            }
            clearInterval(this.$_progressTimer);
        }
    }
</script>
